/**
 * Screen 1j — the estimator for missing curves.
 *
 * Two halves, in an order that insists: **train first, estimate second**. The
 * estimate button stays locked until a model exists, and every model carries
 * its blind-well figure wherever it is shown. An interface that lets someone
 * estimate without ever seeing how bad the model is, is an interface that
 * produces numbers with no provenance.
 */
import { useEffect, useMemo, useState } from "react";
import { api, unpage, type Page } from "../api";
import { canCompute } from "../auth";
import {
  Button,
  Checkbox,
  Dropdown,
  Empty,
  Loading,
  Modal,
  Notice,
  NumberField,
  Panel,
  Segmented,
  Slider,
  TextField,
  Toggle,
  useToast,
} from "../components/ui";
import { DeleteDialog } from "../components/DeleteDialog";
import { EstimateComparison } from "../components/EstimateComparison";
import { EstimateWellLog } from "../components/EstimateWellLog";
import { RailCollapseButton, RailStrip, type RailTabItem } from "../components/RailTabs";
import { Resizer } from "../components/Resizer";
import ScreeningMap from "../components/ScreeningMap";
import { useAsync, useStored, useWellSync } from "../hooks";
import { count } from "../lib/plural";
import type {
  EstimateApplied,
  EstimateCandidates,
  LearnerMethod,
  LogCompare,
  LogModel,
  Project,
  Screening,
  Well,
  WellNeighbours,
} from "../types";

/** The curves usually present, and the ones usually missing. */
/** Display order. Curves outside this list still appear, after it. */
const PREFERRED = ["GR", "RHOB", "NPHI", "DTC", "RT", "PEF", "CALI", "SP", "DRHO",
                   "PHIE", "VSH", "SW", "PERM", "LITHOFACIES"];

/**
 * The selectable curves come from the project's wells, not from a fixed list.
 *
 * A fixed list hides what is actually there. In this project `LITHOFACIES` is
 * present in all twenty wells and is the one curve that makes the
 * classification methods useful — but because it was not on the list, the
 * entire classification path was unreachable from the screen. A capability
 * that exists but cannot be touched is the same as no capability at all.
 */
/**
 * Masukan semu: kedalaman terukur tiap sampel (`mlestimate.DEPTH_INPUT`).
 *
 * Pada selang dangkal yang belum terkompaksi hubungan antar kurva bergeser
 * dengan kedalaman, dan uji sumur buta DTS di Mangunjaya memperlihatkan MD
 * menaikkan R² di atas DTC saja. Ia bukan kurva, jadi tidak pernah muncul di
 * daftar kurva sumur — ditawarkan di sini secara terpisah.
 */
const DEPTH_INPUT = "MD";

function candidateCurves(wells: Well[]): string[] {
  const counts = new Map<string, number>();
  for (const well of wells) {
    const seen = new Set<string>();
    for (const set of well.curve_sets ?? []) {
      for (const mnemonic of set.mnemonics ?? []) {
        if (!seen.has(mnemonic)) {
          seen.add(mnemonic);
          counts.set(mnemonic, (counts.get(mnemonic) ?? 0) + 1);
        }
      }
    }
  }
  // A curve present in only one well can be neither trained on nor held out.
  const usable = [...counts.entries()].filter(([, n]) => n >= 3).map(([m]) => m);
  return usable.sort((a, b) => {
    const ia = PREFERRED.indexOf(a);
    const ib = PREFERRED.indexOf(b);
    if (ia !== -1 && ib !== -1) return ia - ib;
    if (ia !== -1) return -1;
    if (ib !== -1) return 1;
    return a.localeCompare(b);
  });
}

/** Curves whose values are labels rather than continuous numbers. Matches the
 *  list the server uses in `petro/mlestimate.py`. */
const CATEGORICAL = new Set(["LITHOFACIES", "FORCE_2020_LITHOFACIES_LITHOLOGY",
                             "LITHOLOGY"]);

function fmt(value: number | null | undefined, digits = 3): string {
  return value === null || value === undefined || !Number.isFinite(value)
    ? "—"
    : value.toFixed(digits);
}

/** Kepala dan sel tabel tetangga terjaring, sebentuk dengan tabel layar lain. */
const KEPALA: React.CSSProperties = {
  padding: "5px 7px",
  textAlign: "right",
  font: "600 9px var(--font-mono)",
  color: "var(--ink-4)",
  letterSpacing: ".06em",
  borderBottom: "1px solid var(--line-strong)",
  whiteSpace: "nowrap",
};

const SEL = (align: "left" | "right" = "right"): React.CSSProperties => ({
  padding: "4px 7px",
  textAlign: align,
  font: "400 9.5px var(--font-mono)",
  color: "var(--ink-3)",
  borderBottom: "1px solid var(--line)",
  whiteSpace: "nowrap",
});

/** R² colour: not decoration — this is what says whether the number is usable. */
function scoreTone(r2: number | null | undefined): string {
  if (r2 === null || r2 === undefined) return "var(--ink-5)";
  if (r2 >= 0.85) return "var(--green)";
  if (r2 >= 0.6) return "var(--amber)";
  return "var(--rose)";
}

/**
 * Isi rel kanan layar ini, sebagai daftar tab yang sama bentuknya dengan rel
 * layar log — satu nama, sebab rel ini satu kolom panel dan bukan empat tab.
 */
const RAIL_TABS: RailTabItem<"estimasi">[] = [{ key: "estimasi", label: "Estimation" }];

export default function LogEstimate({
  project,
  well,
  wells,
  onDataChanged,
  onWellChange,
}: {
  project: Project;
  /** Sumur yang sedang aktif di cangkang — dari sinilah pilihan awalnya. */
  well: Well | null;
  wells: Well[];
  onDataChanged?: () => void;
  /**
   * Dipanggil ketika sumur yang ditaksir berpindah, supaya pemilih WELL di
   * cangkang menunjuk sumur yang sama. Lihat `useWellSync`.
   */
  onWellChange?: (wellId: number) => void;
}) {
  const toast = useToast();
  const mayCompute = canCompute(project.my_role);

  // The method list comes from the server, not hard-coded here: only the place
  // the models are trained knows which libraries are actually installed.
  const methods = useAsync(
    (signal) => api.get<{ methods: LearnerMethod[]; note: string }>(
      "/api/ml-methods/", {}, signal),
    [],
  );
  const models = useAsync(
    (signal) =>
      api
        .get<Page<LogModel>>("/api/log-models/", { project: project.id }, signal)
        .then((page) => unpage(page, signal)),
    [project.id],
  );

  const candidates = useMemo(() => candidateCurves(wells), [wells]);
  // Lebar rel kanan: pemilih kurva masukan, jenis model, dan metriknya.
  const [railWidth, setRailWidth] = useStored("evolog.estimate.rail", 300);
  // Kurva target dan kurva masukannya dipilih sekali, modelnya dilatih, lalu
  // yang dibaca adalah silang-taksir dan kurva hasilnya. Disimpan, dengan
  // alasan yang sama seperti lebarnya sendiri.
  const [railCollapsed, setRailCollapsed] = useStored("evolog.estimate.rail.collapsed", false);
  const [target, setTarget] = useState("DTC");
  const [inputs, setInputs] = useState<string[]>(["GR", "RHOB", "NPHI", "RT"]);
  const [kind, setKind] = useState<string>("gradient_boosting");
  /**
   * Nama yang dipakai menyimpan model berikutnya.
   *
   * Sebuah model memang tersimpan sejak ia dilatih — tetapi tersimpan dengan
   * nama yang menggambarkan hitungannya sendiri, "DTC from GR+RHOB+NPHI", dan
   * sesudah selusin percobaan daftarnya memuat dua belas nama yang berbunyi
   * nyaris sama. Yang membedakan satu percobaan dari yang lain bukan rumusnya
   * melainkan alasannya: "tanpa sumur selatan", "sesudah RHOB dikoreksi". Itu
   * hanya bisa dikatakan oleh yang melatihnya, pada saat ia melatihnya.
   *
   * Kosong tetap sah: namanya lalu dibuat dari rumusnya seperti dahulu, sebab
   * sebuah kotak yang harus diisi sebelum tombolnya hidup adalah pajak atas
   * percobaan yang memang belum tentu disimpan lama.
   */
  const [modelName, setModelName] = useState("");
  const [compare, setCompare] = useState<LogCompare | null>(null);
  const [comparing, setComparing] = useState(false);
  const chosen = methods.data?.methods.find((m) => m.key === kind) ?? null;

  const [half, setHalf] = useState(4);
  const [allRows, setAllRows] = useState(false);
  const [blind, setBlind] = useState<Set<number>>(new Set());
  const [training, setTraining] = useState(false);
  const [selected, setSelected] = useState<number | null>(null);
  const [predictWell, setPredictWell] = useState<number | null>(null);
  /**
   * Sumur yang ditaksir dan pemilih WELL di cangkang menunjuk sumur yang sama.
   *
   * Sebelum ini ia mulai kosong, dan kekosongan itu mengunci dua tombol
   * sekaligus — "Compare with the measured curve" dan "Estimate and save the
   * curve" — tanpa satu kata pun yang mengatakan mengapa. Yang terbaca adalah
   * model yang sudah terlatih tetapi tidak bisa dipakai, dan yang dicurigai
   * adalah pelatihannya, bukan sebuah dropdown di bawahnya yang bertuliskan
   * "—". Separuh atas layar ini memilih bawaannya sendiri; separuh bawahnya
   * tidak boleh menjadi satu-satunya yang menunggu tanpa memberi tahu.
   *
   * Dahulu pilihan itu hanya pilihan **awal**: sesudah terisi, pergantian
   * sumur di cangkang tidak menariknya lagi, dengan alasan bahwa sumur yang
   * ditaksir boleh berbeda dari sumur yang sedang dilihat. Yang tidak terpikir
   * di sana adalah arah sebaliknya — memilih KTI-07 di dropdown ini
   * meninggalkan pemilih di cangkang menunjuk KTI-01, dan tombol CPI A3 di
   * sebelahnya mencetak KTI-01 sementara seluruh layar membaca KTI-07.
   * Keduanya sekarang satu, dua arah, dan yang dikatakan pemilih di atas
   * selalu sumur yang sedang dikerjakan layar ini.
   *
   * Sumur tetangga yang ikut melatih model tidak menggeser apa pun: yang
   * dilaporkan ke atas adalah sumur yang ditaksir, dan hanya itu.
   */
  useWellSync(predictWell, well?.id ?? null, {
    onFocus: onWellChange,
    onAdopt: setPredictWell,
  });
  useEffect(() => {
    if (predictWell === null && well) setPredictWell(well.id);
  }, [well, predictWell]);
  /**
   * Menerapkan model yang sudah jadi ke sumur lain.
   *
   * Melatih dan menerapkan adalah dua pekerjaan yang berbeda, dan sampai
   * sekarang layar ini hanya bisa mengerjakan yang pertama lalu menaksir satu
   * sumur. Yang ditanyakan orang sesudah modelnya baik adalah pertanyaan yang
   * lain: sumur mana di lapangan ini yang kurvanya belum ada atau berlubang,
   * dan berapa di antaranya yang bisa ditolong sekarang.
   */
  const [applyPick, setApplyPick] = useState<Set<number> | null>(null);
  const [applied, setApplied] = useState<EstimateApplied | null>(null);
  const [applying, setApplying] = useState(false);
  /**
   * Penjaringan radius: kolam sumur yang boleh dipelajari model dibatasi ke
   * lingkaran di sekeliling sumur yang ditaksir.
   *
   * Seluruh sumur proyek adalah tawaran yang benar di lapangan yang sumurnya
   * menembus penampang yang sama. Ia menjadi salah begitu lapangannya luas:
   * model yang dipas pada sumur empat puluh kilometer jauhnya, di seberang
   * sesar dan pergantian fasies, tetap melaporkan nilai butanya — hanya saja
   * nilai itu diperoleh di tempat yang bukan tempat sumur yang ditaksir
   * berdiri. Radiusnya disetel, sebab jarak yang masuk akal di delta bukan
   * jarak yang masuk akal di paparan karbonat.
   */
  const [screening, setScreening] = useStored("evolog.estimate.radius.on", false);
  const [radiusKm, setRadiusKm] = useStored("evolog.estimate.radius.km", 5);
  /**
   * Bentuk jaringnya. Lima layar lain sudah menurunkan pusat lingkarannya
   * mengikuti lintasan acuan; layar ini tidak boleh menjadi satu-satunya yang
   * membiarkannya berdiri diam di kepala sumur, sebab dua layar yang menjawab
   * "sumur mana yang dekat" dengan cara berbeda adalah dua layar yang memilih
   * dua kolam latih yang berbeda.
   */
  const [radiusMode, setRadiusMode] = useStored<"surface" | "trajectory">(
    "evolog.estimate.radius.mode", "trajectory");
  // Neighbour analysis for the well to be estimated. Estimation always borrows
  // from neighbours, and "neighbour" in log work means two things at once:
  // close at surface, and penetrating the same depth interval.
  const neighbours = useAsync(
    (signal) =>
      predictWell
        ? api.get<WellNeighbours>(
            `/api/wells/${predictWell}/neighbours/`,
            {
              target,
              inputs: inputs.join(","),
              ...(screening
                ? { radius: Math.round(radiusKm * 1000), radius_mode: radiusMode }
                : {}),
            },
            signal,
          )
        : Promise.resolve(null),
    [predictWell, target, inputs.join(","), screening, radiusKm, radiusMode],
  );
  /**
   * Jaring yang sama, sekali lagi, dalam bentuk yang bisa digambar.
   *
   * Tabel tetangga menjawab "berapa jauh KTI-11 A dari sumur ini". Yang tidak
   * bisa dijawabnya adalah pertanyaan yang justru ditanyakan lebih dulu,
   * sebelum radiusnya disetel: **bentuk** dari yang terjaring. Enam nama dan
   * enam jarak tidak memperlihatkan bahwa keenamnya duduk di satu sisi
   * lingkaran sementara sisi seberangnya kosong — dan model yang dilatih dari
   * setengah lingkaran adalah model dari setengah lapangan. Tidak juga
   * memperlihatkan sumur ketujuh yang berdiri seratus meter di luar garis,
   * yaitu keterangan yang menggeser radius seseorang dari 5 km menjadi 5,2 km.
   *
   * Yang ditanyakan adalah endpoint penjaringan yang sudah dipakai empat layar
   * lain, dan bukan hasil pengukuran ulang di sini. Keduanya berpulang ke
   * `crossplot.screen()` yang sama, jadi warna di peta adalah keputusan yang
   * sama dengan yang memilih kolam latihnya — sebuah peta yang memutuskan
   * sendiri siapa yang di dalam lingkaran cepat atau lambat akan berselisih
   * dengan sumur yang benar-benar dikirim melatih, terutama pada mode
   * `trajectory` tempat yang menentukan bukan jarak antar kepala sumur.
   */
  const net = useAsync(
    (signal) =>
      api.get<Screening>(
        `/api/projects/${project.id}/screening/`,
        {
          center_well: predictWell,
          radius: Math.round(radiusKm * 1000),
          radius_mode: radiusMode,
        },
        signal,
      ),
    [project.id, predictWell, radiusKm, radiusMode],
    { skip: !screening || predictWell === null },
  );
  /** Seluruh sumur lapangan bagi petanya, bukan hanya yang lolos: yang di luar
   *  lingkaran adalah separuh dari apa yang dikatakan peta ini. */
  const mapWells = useMemo(
    () => wells.map((w) => ({
      id: w.id, name: w.name, x: w.surface_x, y: w.surface_y,
    })),
    [wells],
  );
  const [result, setResult] = useState<Record<string, unknown> | null>(null);
  const [busy, setBusy] = useState(false);

  /**
   * Kurva yang dikehendaki, yang menyaring pustaka model di bawahnya.
   *
   * Sebuah model terikat pada satu kurva target, jadi pertanyaan "kurva mana
   * yang ingin saya taksir" mendahului pertanyaan "model yang mana". Sampai
   * sekarang urutannya terbalik: daftar yang ditawarkan memuat seluruh model
   * proyek ini, dan yang mencari penaksir DTC harus mengenali sendiri mana di
   * antara tiga puluh nama yang menaksir DTC.
   *
   * `"*"` berarti seluruhnya — keadaan yang benar bagi proyek yang modelnya
   * masih sedikit, dan itulah keadaan awalnya.
   */
  const [wanted, setWanted] = useState("*");
  /** Kurva yang benar-benar punya model, dengan berapa banyak masing-masing. */
  const savedTargets = useMemo(() => {
    const counts = new Map<string, number>();
    for (const m of models.data ?? []) counts.set(m.target, (counts.get(m.target) ?? 0) + 1);
    return [...counts.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [models.data]);
  const saved = useMemo(
    () => (models.data ?? []).filter((m) => wanted === "*" || m.target === wanted),
    [models.data, wanted],
  );
  // Saringan yang tidak lagi menjaring apa pun — modelnya yang terakhir baru
  // saja dibuang — akan memperlihatkan layar kosong yang menyuruh melatih model
  // baru, padahal yang perlu dilakukan hanya melepas saringannya. Dilepas
  // sendiri, sebab sebabnya tidak terbaca dari layar yang kosong itu.
  useEffect(() => {
    if (wanted !== "*" && models.data && !models.data.some((m) => m.target === wanted))
      setWanted("*");
  }, [models.data, wanted]);

  const model = saved.find((m) => m.id === selected) ?? saved[0] ?? null;
  /** Model yang sedang diganti namanya, dan nama barunya selama diketik. */
  const [renaming, setRenaming] = useState<LogModel | null>(null);
  const [newName, setNewName] = useState("");
  const [discarding, setDiscarding] = useState<LogModel | null>(null);
  const [managing, setManaging] = useState(false);

  /** Sumur yang membutuhkan kurva ini, dan yang sanggup menerimanya. */
  const needy = useAsync(
    (signal) =>
      api.get<EstimateCandidates>(
        `/api/log-models/${model!.id}/candidates/`, undefined, signal),
    [model?.id],
    { skip: !model },
  );

  /**
   * Pilihan awal: tidak ada satu pun.
   *
   * Dahulu daftar ini datang dengan seluruh sumur yang butuh **dan** sanggup
   * sudah tercentang, dan tombol di bawahnya langsung berbunyi "Estimate 9
   * wells". Yang dikerjakan sebuah panel yang terbuka dengan sembilan centang
   * adalah menulis kurva pada sembilan sumur atas nama orang yang baru saja
   * membukanya untuk melihat isinya. Menaksir bukan tindakan yang boleh
   * dimulai oleh sebuah panel yang terbuka sendiri; yang memilih sumurnya
   * harus orangnya, satu per satu, dan daftarnya tetap memberi tahu mana yang
   * butuh, mana yang sanggup, dan mana yang sudah punya taksiran.
   *
   * Tetap dipasang ulang ketika daftarnya berganti, supaya centang atas sumur
   * yang sudah tidak ada di daftar berikutnya tidak ikut terbawa.
   */
  useEffect(() => {
    if (!needy.data) return;
    setApplyPick(new Set());
  }, [needy.data]);

  // Ringkasan hasil dibuang ketika **modelnya** berganti, bukan ketika daftar
  // kandidatnya dimuat ulang. Keduanya sempat satu efek, dan karena menerapkan
  // model justru memuat ulang daftar itu, ringkasan yang baru saja ditulis
  // terhapus sendiri sepersekian detik kemudian — hasilnya berkedip lalu
  // lenyap, dan yang terbaca adalah tombol yang seolah tidak melakukan apa-apa.
  useEffect(() => {
    setApplied(null);
  }, [model?.id]);

  const applyModel = async () => {
    if (!model || !applyPick?.size) return;
    setApplying(true);
    try {
      setApplied(await api.post<EstimateApplied>(
        `/api/log-models/${model.id}/apply/`, { wells: [...applyPick] }));
      needy.reload();
      onDataChanged?.();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : String(cause), "error");
    } finally {
      setApplying(false);
    }
  };

  const rename = async () => {
    if (!renaming || !newName.trim()) return;
    setManaging(true);
    try {
      await api.patch<LogModel>(`/api/log-models/${renaming.id}/`,
                                { name: newName.trim() });
      await models.reload();
      setRenaming(null);
      toast(`Model dinamai “${newName.trim()}”.`);
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : String(cause), "error");
    } finally {
      setManaging(false);
    }
  };

  /**
   * Membuang satu model dari pustaka.
   *
   * Yang dibuang adalah bobotnya, dan hanya itu: kurva yang sudah ditaksir
   * dengannya adalah kurva biasa di setnya sendiri dan tetap berdiri. Itu
   * urutan yang benar — model yang dibuang setahun sesudah taksirannya tidak
   * boleh membawa taksiran itu serta — dan dikatakan di dialognya, sebab yang
   * menekan tombolnya sedang menebak justru hal ini.
   */
  const discard = async () => {
    if (!discarding) return;
    setManaging(true);
    try {
      await api.delete(`/api/log-models/${discarding.id}/`);
      if (selected === discarding.id) setSelected(null);
      await models.reload();
      toast(`“${discarding.name}” dibuang. Kurva yang sudah ditaksir dengannya tetap ada.`);
      setDiscarding(null);
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : String(cause), "error");
    } finally {
      setManaging(false);
    }
  };

  const usable = useMemo(() => inputs.filter((c) => c !== target), [inputs, target]);

  const referenceName = wells.find((w) => w.id === predictWell)?.name ?? null;
  /**
   * Sumur yang lolos jaring, sebagai id — dan `null` ketika tidak ada jaring
   * sama sekali, yang berarti seluruh sumur proyek seperti sediakala.
   *
   * Daftar kosong bukan `null`: jaring yang terlalu rapat berbeda dari tidak
   * ada jaring, dan servernya menjawab keduanya dengan kalimat yang berlainan.
   */
  const pool = useMemo(() => {
    if (!screening || predictWell === null || !neighbours.data) return null;
    return neighbours.data.neighbours
      .filter((n) => n.within_radius)
      .map((n) => n.well_id);
  }, [screening, predictWell, neighbours.data]);
  const poolIds = useMemo(() => new Set(pool ?? []), [pool]);
  /**
   * Payload yang benar-benar sudah menjaring, dan bukan sekadar payload yang
   * ada. Radius yang dipakainya adalah radius payload-nya sendiri, bukan yang
   * sedang diketik: keduanya berbeda selama permintaannya masih di jalan.
   *
   * Saklar jaring yang baru dinyalakan membiarkan jawaban tanpa-radius
   * sebelumnya tetap terpasang sepersekian detik. Membacanya sebagai hasil
   * penjaringan berarti memajang "tidak ada sumur yang terjaring" pada
   * lapangan yang sebenarnya belum ditanya. `radius_m` yang terisi adalah
   * tanda bahwa jawabannya memang jawaban atas sebuah jaring.
   */
  const screened = useMemo(
    () => (screening && neighbours.data?.radius_m != null ? neighbours.data : null),
    [screening, neighbours.data],
  );
  /** Sumur yang lolos jaring, dengan jaraknya — isi tabel Neighbouring wells. */
  const caught = useMemo(
    () => (screened?.neighbours ?? []).filter((n) => n.within_radius),
    [screened],
  );

  /** Apa yang sedang dijaring, dikatakan di sebelah tombol yang memakainya. */
  const poolLine = useMemo(() => {
    if (!screening) return null;
    if (predictWell === null)
      return ("The filter centres on the well being estimated, and none is picked "
              + "yet — pick one in the Estimate panel first. Until then it filters "
              + "nothing, and the model is trained over every well in the "
              + "project.");
    if (!neighbours.data) return "Working out the distances…";
    return (`${count(pool?.length ?? 0, "well")} within ${radiusKm} km of `
            + `${referenceName}, ${neighbours.data.usable_within_radius} of them `
            + `carrying ${target} together with every input curve. `
            + `${referenceName} itself is not trained on.`);
  }, [screening, predictWell, neighbours.data, pool, radiusKm, referenceName, target]);

  const train = async () => {
    setTraining(true);
    setResult(null);
    try {
      const created = await api.post<LogModel>("/api/log-models/", {
        project: project.id,
        target,
        inputs: usable,
        kind,
        half_window: half,
        ...(allRows ? { all_rows: true } : {}),
        // Namanya hanya dikirim kalau memang ditulis: kosong berarti server
        // menamainya dari rumusnya, dan itu keputusan yang sudah ada di sana.
        ...(modelName.trim() ? { name: modelName.trim() } : {}),
        blind_wells: wells.filter((w) => blind.has(w.id)).map((w) => w.name),
        // Radiusnya ikut dikirim bersama daftarnya, bukan hanya daftarnya.
        // Sebuah model yang menyimpan sepuluh nama tanpa menyimpan sebab
        // kesepuluhnya yang terpilih adalah percobaan yang tidak bisa diulang.
        ...(pool
          ? {
              wells: pool,
              radius_m: Math.round(radiusKm * 1000),
              reference_well: predictWell,
            }
          : {}),
      });
      await models.reload();
      // Yang baru dilatih menjadi yang terpilih, dan saringan kurva yang
      // kebetulan menyembunyikannya dilepas: sebuah model yang tidak terlihat
      // sesudah tombolnya ditekan terbaca seperti pelatihan yang gagal.
      if (wanted !== "*" && wanted !== created.target) setWanted("*");
      setSelected(created.id);
      // Namanya sudah terpakai. Membiarkannya berdiri di kotaknya membuat
      // percobaan berikutnya lahir dengan nama percobaan ini.
      setModelName("");
      toast(
        `“${created.name}” disimpan — blind-well R² ${fmt(created.blind_r2)} on `
        + `${count(created.wells_blind.length, "well")}.`,
      );
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : String(cause), "error");
    } finally {
      setTraining(false);
    }
  };

  const predict = async () => {
    if (!model || predictWell === null) return;
    setBusy(true);
    try {
      const out = await api.post<Record<string, unknown>>(
        `/api/log-models/${model.id}/predict/`,
        { well: predictWell },
      );
      setResult(out);
      onDataChanged?.();
      toast(`${out.curve} written to ${out.well}.`);
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : String(cause), "error");
    } finally {
      setBusy(false);
    }
  };

  const cell: React.CSSProperties = {
    padding: "4px 9px",
    textAlign: "right",
    font: "400 10.5px var(--font-mono)",
    borderBottom: "1px solid var(--line-soft)",
    whiteSpace: "nowrap",
  };
  const head: React.CSSProperties = {
    ...cell,
    font: "600 9.5px var(--font-mono)",
    color: "var(--ink-5)",
    borderBottom: "1px solid var(--line-strong)",
  };

  return (
    <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
      <div className="scroll-y" style={{ flex: 1, minWidth: 0, padding: 14 }}>
        {training && <Loading label="Training on the surrounding wells…" />}

        {!training && !model && (
          <Empty>
            {/* Namanya, bukan tempatnya — alasan yang sama seperti di mode
                Batch layar analysis: rel ini bisa terlipat, dan "on the right"
                lalu menunjuk ke pitanya, bukan ke panelnya. */}
            No model yet. Pick a target curve and its input curves in the Estimate panel,
            then train on the wells that carry both.
          </Empty>
        )}

        {!training && model && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
              <span style={{ font: "600 12px var(--font-sans)", color: "var(--ink)" }}>
                {model.name}
              </span>
              <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-5)" }}>
                {model.kind_label} · ±{model.half_window} sample window
              </span>
              <span style={{ flex: 1 }} />
              {/* Classification models have no R². Showing "blind R² —" for a
                  lithofacies model makes it look like a failure when
                  akurasinya justru terukur. */}
              <span
                style={{
                  font: "600 14px var(--font-mono)",
                  color: scoreTone(model.blind_score?.value ?? model.blind_r2),
                }}
              >
                {model.blind_score?.label ?? "Blind R²"}{" "}
                {fmt(model.blind_score?.value ?? model.blind_r2)}
              </span>
              {model.blind_score?.extra !== null &&
                model.blind_score?.extra !== undefined && (
                  <span style={{ font: "400 11px var(--font-mono)", color: "var(--ink-5)" }}>
                    {model.blind_score.extra_label} {fmt(model.blind_score.extra)}
                  </span>
                )}
            </div>

            <Notice tone={(model.blind_score?.value ?? -1) >= 0.6 ? "info" : "warn"}>
              {model.metrics?.note}
            </Notice>

            {/* The model task decides which metrics mean anything, so the columns follow. */}
            <div style={{ border: "1px solid var(--line)", borderRadius: 7, overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", background: "var(--panel-inset)" }}>
                <thead>
                  <tr>
                    <th style={{ ...head, textAlign: "left" }}>Well</th>
                    <th style={{ ...head, textAlign: "left" }}>Role</th>
                    <th style={head}>Samples</th>
                    {/* Kolomnya mengikuti tugas modelnya. Tabel klasifikasi
                        still headed R², RMSE, MAE would show only
                        tiga kolom tanda pisah — pembacanya lalu mengira
                        modelnya gagal, padahal metriknya memang lain. */}
                    {model.task === "classification" ? (
                      <>
                        <th style={head}>Akurasi</th>
                        <th style={head}>F1 makro</th>
                        <th style={head}>Kelas</th>
                      </>
                    ) : (
                      <>
                        <th style={head}>R²</th>
                        <th style={head}>RMSE</th>
                        <th style={head}>MAE</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {(model.metrics?.per_well ?? []).map((row) => (
                    <tr key={row.well}>
                      <td style={{ ...cell, textAlign: "left", color: "var(--ink-2)" }}>{row.well}</td>
                      <td
                        style={{
                          ...cell,
                          textAlign: "left",
                          color: row.blind ? "var(--accent)" : "var(--ink-6)",
                        }}
                      >
                        {row.blind ? "blind test" : "train"}
                      </td>
                      <td style={cell}>{row.n.toLocaleString("en-US")}</td>
                      {model.task === "classification" ? (
                        <>
                          <td style={{ ...cell, color: row.blind ? scoreTone(row.accuracy) : "var(--ink-4)" }}>
                            {fmt(row.accuracy)}
                          </td>
                          <td style={cell}>{fmt(row.f1_macro)}</td>
                          <td style={cell}>{row.classes ?? "—"}</td>
                        </>
                      ) : (
                        <>
                          <td style={{ ...cell, color: row.blind ? scoreTone(row.r2) : "var(--ink-4)" }}>
                            {fmt(row.r2)}
                          </td>
                          <td style={cell}>{fmt(row.rmse, 2)}</td>
                          <td style={cell}>{fmt(row.mae, 2)}</td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {compare && (
              <div
                style={{
                  marginTop: 14,
                  paddingTop: 14,
                  borderTop: "1px solid var(--line)",
                }}
              >
                <EstimateComparison data={compare} />
              </div>
            )}

            {/* Pajangan lembar log berdiri sendiri di bawah perbandingan satu
                sumur: yang pertama menjawab "seberapa jauh melesetnya di sini",
                yang kedua "apakah sumur buta ini terlihat seperti sumur
                latihnya" — dan pertanyaan kedua hanya bisa dijawab kalau
                keduanya tergambar bersebelahan. */}
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--line)" }}>
              <EstimateWellLog model={model} wells={wells} />
            </div>

            {(model.metrics?.skipped ?? []).length > 0 && (
              <span style={{ font: "400 10px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.6 }}>
                Dilewati:{" "}
                {(model.metrics?.skipped ?? [])
                  .map((s) => `${s.well} (${s.reason})`)
                  .join("; ")}
                .
              </span>
            )}

            {result && (
              <Panel title="Estimation result">
                <span style={{ font: "400 10.5px var(--font-mono)", color: "var(--ink-3)", lineHeight: 1.8 }}>
                  {String(result.curve)} ditulis ke {String(result.well)}, versi{" "}
                  {String(result.version)}
                  <br />
                  {Number(result.estimated).toLocaleString()} of{" "}
                  {Number(result.samples).toLocaleString("en-US")} samples estimated (
                  {(Number(result.coverage) * 100).toFixed(1)} %)
                  {result.comparison_with_measured
                    ? ` · against the measured curve in this well: R² ${fmt(
                        (result.comparison_with_measured as { r2: number }).r2,
                      )}`
                    : " · this well has no measured curve to begin with"}
                </span>
                {/* Sebuah set yang memuat lebih banyak daripada yang diminta
                    harus mengatakannya, kalau tidak ia tidak bisa dibedakan
                    dari sisa yang terlupa dibersihkan. */}
                {Array.isArray(result.carried_forward) && result.carried_forward.length > 0 && (
                  <span style={{ font: "400 10px var(--font-mono)", color: "var(--ok-ink)", lineHeight: 1.6 }}>
                    Taksiran yang sudah ada di sumur ini ikut pindah ke versi ini:{" "}
                    {(result.carried_forward as string[]).join(", ")}.
                  </span>
                )}
                <span style={{ font: "400 10px var(--font-sans)", color: "var(--warn-ink)", lineHeight: 1.6 }}>
                  The curve is written to the “ESTIMATED” set with an <code>_EST</code>
                  suffix, kept apart from the measured curves. Once the two are confused,
                  there is no way to tell which numbers were ever measured. It is a
                  curve like any other from here on: the deterministic workflow, the
                  crossplots and the exports all read it.
                </span>
              </Panel>
            )}
          </div>
        )}
      </div>

      {/* Pegangan lebarnya hanya ada selama ada yang bisa dilebarkan. */}
      {!railCollapsed && (
        <Resizer
          width={railWidth}
          onChange={setRailWidth}
          min={250}
          max={560}
          label="Resize the estimation panel"
        />
      )}
      {railCollapsed ? (
        <RailStrip
          tabs={RAIL_TABS}
          active="estimasi"
          onSelect={() => setRailCollapsed(false)}
          expandLabel="Show the estimation panel"
        />
      ) : (
        <aside
          style={{
            width: railWidth,
            flex: "none",
            background: "var(--rail)",
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
          }}
        >
          {/* Kepala rel, yang tidak ikut bergulir: dua panelnya lebih tinggi
              daripada layar, dan tombol lipat yang hanyut bersama daftar kurva
              adalah tombol yang harus digulirkan kembali dulu. */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "5px 7px 5px 12px",
              borderBottom: "1px solid var(--line-soft)",
              flex: "none",
            }}
          >
            <span className="label">Estimation</span>
            <RailCollapseButton
              side="right"
              collapsed={false}
              label="Hide the estimation panel"
              onClick={() => setRailCollapsed(true)}
            />
          </div>
          <div
            className="scroll-y"
            style={{
              flex: 1,
              padding: 12,
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            <Panel
              title="Train"
              meta={pool
                ? `${pool.length}/${count(wells.length, "well")}`
                : count(wells.length, "well")}
            >
              <Dropdown
                label="Target curve"
                value={target}
                onChange={setTarget}
                options={candidates.map((c) => ({
                  value: c,
                  // Labelled curves are marked so it is no surprise when the
                  // metric that comes back is accuracy rather than R².
                  label: CATEGORICAL.has(c) ? `${c} — berlabel` : c,
                }))}
              />
              <span style={{ font: "600 9px var(--font-mono)", color: "var(--ink-6)" }}>
                INPUT CURVES
              </span>
              {[...candidates.filter((c) => c !== target && !CATEGORICAL.has(c) && c !== DEPTH_INPUT),
                DEPTH_INPUT].map((c) => (
                <Checkbox
                  key={c}
                  label={c === DEPTH_INPUT ? "MD — kedalaman sampel (bukan kurva)" : c}
                  checked={inputs.includes(c)}
                  onChange={(on) =>
                    setInputs((prev) =>
                      on ? [...new Set([...prev, c])] : prev.filter((x) => x !== c),
                    )
                  }
                />
              ))}
              <Dropdown
                label="Method"
                value={kind}
                onChange={setKind}
                options={(methods.data?.methods ?? []).map((m) => ({
                  value: m.key,
                  // A method whose library is missing is still shown. If XGBoost
                  // simply vanished from the list, people would assume the
                  // application does not support it, when all that is missing is
                  // one pip command.
                  label: m.available ? m.label : `${m.label} — not installed`,
                }))}
              />
              {chosen && (
                <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.6 }}>
                  {chosen.note}
                  {chosen.row_cap && !allRows
                    ? ` Training rows capped at ${chosen.row_cap.toLocaleString()}.`
                    : ""}
                </span>
              )}
              {/* Batasnya menjaga pelatihan tetap cepat, tetapi bukan batas
                  kemampuan metodenya. Siapa yang ingin tahu seberapa jauh
                  sebuah metode bisa melaju boleh melepasnya. */}
              {chosen?.row_cap ? (
                <Toggle
                  label="Train on every row (no cap)"
                  checked={allRows}
                  onChange={setAllRows}
                />
              ) : null}
              {allRows && (chosen?.key === "svm" || chosen?.key === "knn") ? (
                <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--warn-ink)", lineHeight: 1.6 }}>
                  Kernel methods grow with the square of the row count. Beyond
                  tens of thousands of rows, training can take hours.
                </span>
              ) : null}
              {chosen && !chosen.available && (
                <span style={{ font: "400 9.5px var(--font-mono)", color: "var(--warn-ink)" }}>
                  Install with: {chosen.install}
                </span>
              )}
              {chosen?.family === "classification" && (
                <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--warn-ink)", lineHeight: 1.6 }}>
                  This method emits classes, not continuous numbers. It applies only
                  to labelled curves such as LITHOFACIES.
                </span>
              )}
              <Slider
                label="Half window"
                value={half}
                min={0}
                max={12}
                step={1}
                onChange={(value) => setHalf(Math.round(value))}
                format={(value) => `±${Math.round(value)} samples`}
              />
              {/* Penjaringan radius berdiri tepat di atas tombolnya: yang
                  disetel di sini bukan cara modelnya dipas, melainkan batuan
                  mana yang boleh mengajarinya. */}
              <Toggle
                label="Filter training wells by radius"
                checked={screening}
                onChange={setScreening}
              />
              {screening && (
                <>
                  <NumberField
                    label="Radius"
                    unit="km"
                    value={radiusKm}
                    min={0.1}
                    max={500}
                    step={0.5}
                    onChange={setRadiusKm}
                  />
                  <Segmented
                    value={radiusMode}
                    size="sm"
                    options={[
                      { value: "surface", label: "Well head" },
                      { value: "trajectory", label: "Along the track" },
                    ]}
                    onChange={(next) => setRadiusMode(next as "surface" | "trajectory")}
                  />
                  <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.6 }}>
                    {radiusMode === "trajectory"
                      ? "The centre of the circle descends along the track of the well being estimated, so a neighbour far away at surface but close at depth still joins the training pool."
                      : "One circle at the head of the well being estimated."}
                  </span>
                  <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.6 }}>
                    {poolLine}
                  </span>
                  {neighbours.data?.radius_note && (
                    <Notice tone="warn">{neighbours.data.radius_note}</Notice>
                  )}
                </>
              )}
              {/* Namanya ditulis sebelum dilatih, bukan sesudahnya: yang tahu
                  mengapa percobaan ini berbeda dari yang sebelumnya adalah
                  orang yang sedang menyetelnya, dan sepuluh menit kemudian ia
                  sudah menyetel yang berikutnya. */}
              <TextField
                label="Save the model as"
                value={modelName}
                onChange={setModelName}
                placeholder={usable.length ? `${target} from ${usable.join("+")}` : target}
              />
              <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.6 }}>
                A trained model is kept in this project and can be applied again later,
                to any well. Left blank, it is named after its own arithmetic — which
                says what it computes and nothing about why this one rather than the
                one beside it.
              </span>
              <Button variant="primary" onClick={train} disabled={!mayCompute || training || !usable.length}>
                {training ? "Training…" : "Train model"}
              </Button>
              <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.6 }}>
                Test wells are chosen automatically when none are ticked below.
              </span>
            </Panel>

            <Panel
              title="Blind test wells"
              meta={blind.size ? `${count(blind.size, "well")} chosen` : "automatic"}
            >
              <div className="scroll-y" style={{ maxHeight: 150, display: "flex", flexDirection: "column", gap: 1 }}>
                {/* Seluruh sumur tetap ditawarkan ketika jaringnya menyala.
                    Yang di luar jaring disebut apa adanya di sebelah namanya —
                    sebuah nama yang lenyap dari daftar tidak mengatakan mengapa
                    ia lenyap, dan di sini sebabnya bisa dicabut kembali dengan
                    satu putaran radius. */}
                {wells.map((w) => (
                  <Checkbox
                    key={w.id}
                    label={w.name}
                    meta={
                      !pool
                        ? undefined
                        : w.id === predictWell
                          ? "the well being estimated"
                          : poolIds.has(w.id)
                            ? undefined
                            : "outside the radius"
                    }
                    checked={blind.has(w.id)}
                    onChange={(on) =>
                      setBlind((prev) => {
                        const next = new Set(prev);
                        if (on) next.add(w.id);
                        else next.delete(w.id);
                        return next;
                      })
                    }
                  />
                ))}
              </div>
              {pool && (
                <span style={{ font: "400 9px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.5 }}>
                  A well outside the filter is not trained on, so ticking it here does
                  not make it a blind test.
                </span>
              )}
            </Panel>

            <Panel
              title="Estimate"
              meta={models.data?.length ? count(models.data.length, "model") : undefined}
            >
              {/* Kurva dulu, model sesudahnya. Yang datang ke panel ini datang
                  dengan sebuah kurva yang hilang di kepalanya, bukan dengan
                  nomor model. */}
              {savedTargets.length > 1 && (
                <Dropdown
                  label="Curve wanted"
                  value={wanted}
                  onChange={setWanted}
                  options={[
                    { value: "*", label: `Every curve — ${count(models.data?.length ?? 0, "model")}` },
                    ...savedTargets.map(([curve, n]) => ({
                      value: curve,
                      label: `${curve} — ${count(n, "model")}`,
                    })),
                  ]}
                />
              )}
              <Dropdown
                label="Model"
                value={String(model?.id ?? "")}
                onChange={(value) => setSelected(Number(value))}
                options={saved.map((m) => ({
                  value: String(m.id),
                  // Targetnya ikut tertulis: sebuah nama yang dipilih sendiri
                  // boleh berbunyi "sesudah RHOB dikoreksi", dan nama seperti
                  // itu tidak mengatakan kurva apa yang ditaksirnya.
                  label: `${m.name} · ${m.target} · R² ${fmt(m.blind_r2, 2)}`,
                }))}
              />
              {model && (
                <span style={{ font: "400 9.5px var(--font-mono)", color: "var(--ink-6)", lineHeight: 1.6 }}>
                  {model.inputs.join(", ")} → {model.target} · {model.kind_label} ·{" "}
                  {count(model.wells_trained.length, "training well")},{" "}
                  {count(model.wells_blind.length, "blind well")}
                </span>
              )}
              {/* Mengganti nama dan membuang berdiri di sebelah pemilihnya,
                  bukan di layar administrasi tersendiri: keduanya dikerjakan
                  persis pada saat daftarnya sedang dibaca dan ternyata tidak
                  terbaca. */}
              <div style={{ display: "flex", gap: 6 }}>
                <Button
                  variant="ghost"
                  style={{ flex: 1 }}
                  disabled={!mayCompute || !model}
                  onClick={() => {
                    if (!model) return;
                    setNewName(model.name);
                    setRenaming(model);
                  }}
                >
                  Rename
                </Button>
                <Button
                  variant="ghost"
                  style={{ flex: 1 }}
                  disabled={!mayCompute || !model}
                  onClick={() => model && setDiscarding(model)}
                >
                  Discard
                </Button>
              </div>
              <Dropdown
                label="Well to estimate"
                value={String(predictWell ?? "")}
                onChange={(value) => setPredictWell(Number(value))}
                options={wells.map((w) => ({ value: String(w.id), label: w.name }))}
              />
              <Button
                variant="ghost"
                onClick={async () => {
                  if (!model || predictWell === null) return;
                  setComparing(true);
                  setCompare(null);
                  try {
                    setCompare(await api.post<LogCompare>(
                      `/api/log-models/${model.id}/compare/`, { well: predictWell }));
                  } catch (cause) {
                    toast(cause instanceof Error ? cause.message : String(cause), "error");
                  } finally {
                    setComparing(false);
                  }
                }}
                disabled={comparing || !model || predictWell === null}
              >
                {comparing ? "Membandingkan…" : "Compare with the measured curve"}
              </Button>
              <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.6 }}>
                A comparison is only possible on a well that has its measured curve —
                that is, a well that does not need the estimate. That is exactly
                where the estimate can be checked against reality.
              </span>
              <Button
                onClick={predict}
                disabled={!mayCompute || busy || !model || predictWell === null}
              >
                {busy ? "Estimating…" : "Estimate and save the curve"}
              </Button>
            </Panel>

            {/* Tetangga yang terjaring, dan hanya sesudah dijaring.
                Panel ini dahulu memajang seluruh sumur proyek sejak layarnya
                terbuka, terjaring atau tidak, sebagian diredupkan. Daftar
                sepanjang itu terbaca sebagai daftar yang sudah terpilih,
                padahal belum ada satu pun jaring yang ditebar; yang memakainya
                melatih model atas kolam yang disangkanya sudah disaring.
                Kosong sampai jaringnya ditebar adalah keadaan yang jujur —
                dan sesudah ditebar, yang tampil adalah tabel: nama sumurnya,
                jaraknya ke sumur pusat, dan tumpang tindih selangnya. */}
            <Panel
              title="Neighbouring wells"
              meta={
                screened
                  // "Tetangga" dan bukan "sumur", sejak petanya berdiri di
                  // bawah judul ini. Peta menghitung sumur yang ada di dalam
                  // lingkaran — dan sumur yang ditaksir berdiri di titik
                  // pusatnya, jadi ia selalu satu di antaranya. Dua kalimat
                  // yang sama-sama berkata "well caught" dengan dua angka yang
                  // berbeda terbaca sebagai dua jawaban atas satu pertanyaan;
                  // yang paling terang bunyinya pada radius yang tidak
                  // menjaring apa pun, tempat judulnya berkata "0 wells
                  // caught" sementara petanya berkata "1 of 6".
                  ? `${count(caught.length, "neighbour")} caught · `
                    + `${screened.usable_within_radius} usable`
                  : "not screened yet"
              }
            >
              {/* Peta berdiri di atas tabelnya, dan ia tetap digambar ketika
                  tidak satu sumur pun terjaring. "Widen the radius" sendirian
                  tidak pernah mengatakan sampai mana; petanya mengatakannya —
                  lewat "5 more outside this view" di bawah lingkaran yang
                  kosong, dan lewat "Whole field" yang memulangkan kelimanya
                  berikut jaraknya ke dalam satu pandangan. Bingkai "around the
                  circle" memang tidak memuat mereka pada radius sesempit itu:
                  yang dibingkainya adalah lingkarannya dan yang lolos, dan
                  tidak ada yang lolos.

                  Digantung pada `screening` dan bukan pada `net.data` saja:
                  jawaban yang sudah tiba tetap tersimpan sesudah jaringnya
                  dimatikan, dan sebuah peta jaring yang masih berdiri di bawah
                  saklar yang sudah mati adalah jaring yang tidak pernah
                  ditebar. */}
              {screening && predictWell !== null && (
                net.error ? (
                  <Notice tone="warn">{net.error}</Notice>
                ) : net.data ? (
                  <ScreeningMap screening={net.data} wells={mapWells} height={230} />
                ) : null
              )}
              {!screening ? (
                <Empty>
                  No well is caught until the net is cast. Switch on “Filter training
                  wells by radius” above, and the wells it catches are listed here with
                  their distance to the well being estimated.
                </Empty>
              ) : predictWell === null ? (
                <Empty>
                  The net has no centre yet. Pick the well to estimate in the Estimate
                  panel, and the wells around it appear here.
                </Empty>
              ) : !screened ? (
                <Loading label="Working out the distances…" />
              ) : caught.length === 0 ? (
                <Empty>
                  No well lies within {radiusKm} km of {referenceName}. Widen the radius
                  — a model has to be taught by something.
                </Empty>
              ) : (
                <>
                  <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.6 }}>
                    {screened.note}
                  </span>
                  <div className="scroll-y" style={{ maxHeight: 230 }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr>
                          <th style={{ ...KEPALA, textAlign: "left" }}>Well</th>
                          <th style={KEPALA}>Distance</th>
                          <th style={KEPALA}>TVDSS overlap</th>
                        </tr>
                      </thead>
                      <tbody>
                        {caught.map((n) => (
                          <tr key={n.well_id}>
                            <td style={SEL("left")}>
                              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <span
                                  style={{
                                    width: 6, height: 6, flex: "none", borderRadius: 3,
                                    background: n.usable ? "var(--green)" : "var(--ink-7)",
                                  }}
                                />
                                {n.well}
                              </span>
                            </td>
                            <td style={SEL()}>
                              {n.distance_m === null
                                ? "—"
                                : `${(n.distance_m / 1000).toFixed(1)} km`}
                            </td>
                            <td
                              style={{
                                ...SEL(),
                                color: (n.overlap_tvdss_m ?? 0) >= 50
                                  ? "var(--ink-3)"
                                  : "var(--warn-ink)",
                              }}
                            >
                              {n.overlap_tvdss_m === null
                                ? "—"
                                : `${n.overlap_tvdss_m.toFixed(0)} m`}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <span style={{ font: "400 9px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.5 }}>
                    Distance is head to head, measured to {referenceName}.
                    {radiusMode === "trajectory"
                      ? " What catches a well, though, is its track: a well whose head "
                        + "stands beyond the radius still joins if its track comes "
                        + "within it at depth."
                      : ""}{" "}
                    The last column is TVDSS overlap. A distant well that shares the
                    interval is often worth more than a near one that stops above it.
                  </span>
                  {/* Yang tak berkoordinat tidak berdiri di tabel — jaraknya ke
                      sumur pusat memang tidak ada — tetapi namanya disebut, sebab
                      sumur yang lenyap tanpa sebab terbaca sebagai sumur yang jauh,
                      dan yang kurang adalah kepala sumurnya, bukan kedekatannya. */}
                  {screened.unplaced.length > 0 && (
                    <span style={{ font: "400 9px var(--font-mono)", color: "var(--warn-ink)", lineHeight: 1.5 }}>
                      {screened.unplaced.join(", ")} — no coordinates, so the radius
                      cannot judge {screened.unplaced.length === 1 ? "it" : "them"}.
                    </span>
                  )}
                </>
              )}
            </Panel>

            {/* Menerapkan model yang sudah jadi ke sumur lain.
                Melatih dan menerapkan adalah dua pekerjaan yang berbeda, dan
                sampai sekarang layar ini hanya sanggup yang pertama lalu
                menaksir satu sumur pada satu waktu. Yang ditanyakan orang
                sesudah modelnya baik adalah pertanyaan yang lain: sumur mana di
                lapangan ini yang kurvanya belum ada atau berlubang. */}
            {model && needy.data && (
              <Panel
                title={`Apply to other wells — ${needy.data.ready} ready`}
                meta={`${needy.data.needs} ${needy.data.needs === 1 ? "needs" : "need"} `
                      + `${needy.data.target}`}
              >
                {needy.data.needs === 0 ? (
                  <Empty>
                    Every well in this field already carries {needy.data.target} in full.
                    There is nothing to estimate.
                  </Empty>
                ) : (
                  <>
                    <div
                      className="scroll-y"
                      style={{ maxHeight: 220, display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      {needy.data.wells.filter((w) => w.needs).map((w) => (
                        <Checkbox
                          key={w.well_id}
                          checked={applyPick?.has(w.well_id) ?? false}
                          // Yang kurang kurva masukannya tidak bisa dicentang:
                          // mencentangnya berarti menjadwalkan kegagalan, dan
                          // sebabnya sudah tertulis di keterangannya.
                          onChange={(on) =>
                            w.estimable
                            && setApplyPick((prev) => {
                              const next = new Set(prev ?? []);
                              if (on) next.add(w.well_id);
                              else next.delete(w.well_id);
                              return next;
                            })
                          }
                          label={w.well}
                          meta={
                            !w.estimable
                              ? `missing ${w.missing_inputs.join(", ")}`
                              : (w.already_estimated ? "already estimated · " : "")
                                + (w.state === "no curve"
                                  ? "no curve"
                                  : `gappy · ${(100 * (w.coverage ?? 0)).toFixed(0)}% filled`)
                          }
                        />
                      ))}
                    </div>
                    <span style={{ font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.6 }}>
                      Complete means at least{" "}
                      {(100 * needy.data.coverage_threshold).toFixed(0)}% of its samples
                      are filled. The result is written as {needy.data.target}_EST in the
                      ESTIMATED set — an estimated curve and a measured curve never share
                      a name. Curves estimated earlier on the same well stay in the set
                      beside it.
                    </span>
                    <Button
                      onClick={applyModel}
                      disabled={!mayCompute || applying || !applyPick?.size}
                    >
                      {applying
                        ? "Estimating…"
                        : `Estimate ${count(applyPick?.size ?? 0, "well")}`}
                    </Button>
                    {applied && (
                      <>
                        <span style={{ font: "400 9.5px var(--font-mono)", color: "var(--ok-ink)" }}>
                          {applied.results.length} sumur ditaksir · {applied.curve}
                        </span>
                        {applied.results.map((r) => (
                          <span
                            key={r.well_id}
                            style={{ font: "400 9.5px var(--font-mono)", color: "var(--ink-6)" }}
                          >
                            {r.well} v{r.version} · {(100 * r.coverage).toFixed(0)}% terisi
                            {r.carried_forward?.length
                              ? ` · ${r.carried_forward.join(", ")} ikut pindah`
                              : ""}
                          </span>
                        ))}
                        {applied.skipped.map((r) => (
                          <span
                            key={r.well}
                            style={{ font: "400 9.5px var(--font-mono)", color: "var(--warn-ink)" }}
                          >
                            {r.well} — {r.reason}
                          </span>
                        ))}
                      </>
                    )}
                  </>
                )}
              </Panel>
            )}
          </div>
        </aside>
      )}

      {/* Tetapnya di dalam tapak ini, bukan di akar aplikasi: keduanya berlapis
          `position: fixed`, jadi tempat menulisnya tidak menentukan tempat
          tergambarnya. */}
      {renaming && (
        <Modal
          title="Rename the model"
          width={420}
          onClose={() => setRenaming(null)}
          footer={
            <>
              <Button onClick={() => setRenaming(null)}>Cancel</Button>
              <Button
                variant="primary"
                busy={managing}
                disabled={!newName.trim() || newName.trim() === renaming.name}
                onClick={rename}
              >
                Rename
              </Button>
            </>
          }
        >
          <TextField label="Name" value={newName} onChange={setNewName} autoFocus />
          <span style={{ font: "400 10.5px var(--font-sans)", color: "var(--ink-5)", lineHeight: 1.6 }}>
            Only the name moves. What the model learned — its target curve, its input
            curves, its window and its blind-well score — is the record of one training
            run, and a model whose description no longer matches its weights is worse
            than an awkward name.
          </span>
        </Modal>
      )}

      {discarding && (
        <DeleteDialog
          title="Discard the model"
          what={`${discarding.name} (${discarding.target})`}
          consequences={[]}
          keeps={
            `Curves already estimated with it stay where they are — they are ordinary `
            + `curves in their own sets now. What goes is the model itself, so `
            + `${discarding.target}_EST cannot be re-estimated from it afterwards.`
          }
          busy={managing}
          onCancel={() => setDiscarding(null)}
          onConfirm={discard}
        />
      )}
    </div>
  );
}
